<script>
  import { Dialog } from 'bits-ui'
  import { X } from 'lucide-svelte'
  import Button from '@components/buttons/button.svelte'
  import { cn, delay } from '@lib/utils'

  let {
    open = $bindable(false),
    title,
    tabs,
    width = "50rem",
    class: className,
    onClose,
    focusInputName,
    avoidFocus = false,
    forceMount = false,
    onFocus,
    children,
    footer,
    footerArea,
    showClose = true,
    closeOnClickOutside = true,
    closeOnEscape = true,
    hideFooter = false,
    footerButtonSize,
    ...props } = $props()

  let overlayBaseClasses = $derived(cn(`
    fixed inset-0 z-50 bg-gray-600/60
    data-[state=open]:animate-in data-[state=closed]:animate-out
    data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
    data-[state=closed]:hidden
  `))

  let contentBaseClasses = $derived(cn(`
    p-0 bg-white rounded-lg max-h-[calc(95vh-4rem)] flex flex-col
    fixed left-[50%] top-[4rem] z-50
    translate-x-[-50%]
    shadow-lg duration-200
  `))

  let contentAnimationClasses = $derived(cn(`
    data-[state=open]:animate-in data-[state=closed]:animate-out
    data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
    data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
    data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-4
    data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-4
  `))

  let overlayClass = $derived(overlayBaseClasses)
  let contentClass = $derived(cn(contentBaseClasses, contentAnimationClasses))

  let escapeKeydownBehavior = $derived(closeOnEscape ? 'close' : 'ignore')
  let interactOutsideBehavior = $derived(closeOnClickOutside ? 'close' : 'ignore')

  $effect(() => {
    if (!open) {
      setTimeout(() => {
        document.body.style.pointerEvents = 'auto'
        document.body.style.overflow = 'auto'
      }, 200)
    }
  })

  async function onOpenAutoFocus(e) {
    if (avoidFocus) return e.preventDefault()
    if (!focusInputName && !onFocus) return
    e.preventDefault()
    await delay()
    if (focusInputName) {
      const input = document.querySelector(`[name="${focusInputName}"]`)
      input?.focus()
    } else if (onFocus) {
      onFocus()
    }
  }

  function handleClose() {
    open = false
    onClose?.()
  }

</script>

<Dialog.Root bind:open onOpenChange={onClose}>

  <Dialog.Portal>

    <Dialog.Overlay class={overlayClass} {forceMount}>

      <Dialog.Content class={contentClass} {forceMount} {escapeKeydownBehavior} {interactOutsideBehavior} {onOpenAutoFocus} preventScroll={false} {...props} style={{ width, maxWidth: '90vw' }}>

        <!-- Tabs can be with a title or on their own. There is a slightly different header for each. -->
        {#if title}
          <header class="flex flex-col gap-2 items-center justify-center bg-gray-100/80 border-b border-gray-100 p-4 rounded-t-lg">
            <Dialog.Title class="text-gray-600 text-sm">{title}</Dialog.Title>
            {#if tabs}
              <div class="flex items-center gap-2">
                {@render tabs()}
              </div>
            {/if}
          </header>
        {:else if tabs}
          <header class="flex items-center justify-center gap-3 bg-white border-b border-gray-100 pt-[1.2rem] rounded-t-lg">
            {@render tabs()}
          </header>
        {/if}

        <main class={cn("px-10 pt-8 pb-8 overflow-y-auto flex-1 flex flex-col", className)}>
          {@render children?.({ close: () => open = false })}
        </main>

        {#if footerArea}
          {@render footerArea?.({ close: handleClose })}
        {:else if !hideFooter}
          <footer class="flex justify-end gap-2 px-8 pt-3 pb-8 bg-white rounded-b-lg">
            {#if showClose}
              <Button size={footerButtonSize} onclick={handleClose} text="Close" Icon={X} />
            {/if}
            {@render footer?.({ close: handleClose })}
          </footer>
        {/if}

      </Dialog.Content>

    </Dialog.Overlay>

  </Dialog.Portal>

</Dialog.Root>
